import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Box, Heading } from "@chakra-ui/react";
import Button from "../../components/Button";
import Input from "../../components/Input";
import Pagination from "../../components/Pagination";
import useSyncPagination from "../../components/Pagination/hooks/useSyncPagination";
import Table from "../../components/Table";
import { TableDatum } from "../../components/Table/types";
import { Schema } from "../../api/types";
import useGetOrganization from "../../api/useGetOrganization";
import useGetSchemas from "../../api/useGetSchemas";
import { EntityId } from "../../hooks/useEntitySelection";
import useFilter from "../../hooks/useFilter";
import { sizes } from "../../settings/theme";
import collectionCountLabel from "../../utils/collectionCountLabel";
import { typedSxMap } from "../../utils/typedSxMap";
import { tableRowNavigation } from "../../utils/navigation";
import ActionModal from "./components/actionModal";
import CreateEditSchemaForm from "./components/createEditSchemaForm";
import DeleteSchemaForm from "./components/deleteSchemaForm";
import { schemasTable } from "./utils/schemasTable";
import { PiMagnifyingGlassBold, PiTrash } from "react-icons/pi";

const styles = typedSxMap({
  schemas: {
    flexGrow: 1,
    height: "100%",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "var(--m-xs)",
    marginBottom: "var(--m-xs)",
  },
  title: {
    "& h1": {
      marginBottom: 0,
    },
  },
  actions: {
    display: "flex",
    gap: "var(--m-xs)",
    "& .searchInput": {
      marginBottom: 0,
    },
  },
  searchInput: {
    padding: 0,
    border: "none",
    fontSize: "var(--font-size-2)",
  },
  inputWrapper: {
    backgroundColor: "var(--color-input-background)",
    borderRadius: "var(--border-radius-2)",
    outline: "1px solid transparent",
    border: "1px solid var(--color-border)",
    fontWeight: 400,
    display: "flex",
    alignItems: "center",
    minWidth: "270px",
    position: "relative",
    padding: "calc(var(--m-xxs) / 2)",
  },
  inputIcon: {
    padding: "0 var(--m-xxxs)",
    cursor: "pointer",
    display: "flex",
    position: "relative",
  },
  info: {},
  textsPanel: {},
  footer: {},
  table: {
    display: "table",
    overflow: "auto",
  },
  tbody: {
    display: "table-row-group",
  },
  tr: {
    display: "table-row",
  },
  td: {
    display: "table-cell",
    verticalAlign: "middle",
    maxWidth: "300px",
    height: "48px",
  },
  tableName: {
    display: "flex",
    alignItems: "center",
    gap: "var(--m-xs)",
  },
  importerIcon: {
    color: "var(--color-primary)",
  },

  tableWrapper: {
    display: "flex",
    overflowY: "auto",
    padding: "1px",
    marginRight: "-20px",
    paddingRight: "21px",
    maxHeight: "calc(100vh - 118px)",
    height: "auto",
  },
  tableWrapperWithPagination: {
    maxHeight: "calc(100vh - 250px)",
  },
});

export default function Schemas() {
  const navigate = useNavigate();
  const { data: organization } = useGetOrganization();

  const [actionModal, setActionModal] = useState<{
    selectedSchema: Schema | null;
    action: "Create" | "Edit" | "Delete" | null;
  }>({
    action: null,
    selectedSchema: null,
  });

  const workspaceId = organization?.workspaces?.[0]?.id || "";

  const { isLoading, data: schemas } = useGetSchemas(workspaceId);

  const itemsPerPage = 25;

  const { dataFiltered, setFilter } = useFilter<Schema[]>(["name"], schemas || []);
  const { dataPage, page, paginate, totalItems } = useSyncPagination(dataFiltered as any, itemsPerPage);

  const tableData = schemasTable(dataPage as any, (entityId: EntityId, action) =>
    setActionModal({
      selectedSchema: schemas?.find((schema) => schema.id === (entityId as string)) || null,
      action: action === "edit" ? "Edit" : "Delete",
    })
  );

  const modalContent = useMemo(() => {
    switch (actionModal.action) {
      case "Create":
        return (
          <Box>
            <CreateEditSchemaForm
              action={actionModal.action}
              schema={actionModal.selectedSchema}
              workspaceId={workspaceId}
              onSuccess={function (data: any): void {
                setActionModal({ action: null, selectedSchema: null });
              }}></CreateEditSchemaForm>
          </Box>
        );

      case "Edit":
        return (
          <Box>
            <CreateEditSchemaForm
              action={actionModal.action}
              schema={actionModal.selectedSchema}
              workspaceId={workspaceId}
              onSuccess={function (data: any): void {
                setActionModal({ action: null, selectedSchema: null });
              }}></CreateEditSchemaForm>
          </Box>
        );
      case "Delete":
        return (
          <Box>
            <DeleteSchemaForm
              workspaceId={workspaceId}
              schema={actionModal.selectedSchema}
              onSuccess={function (): void {
                setActionModal({ action: null, selectedSchema: null });
              }}></DeleteSchemaForm>
          </Box>
        );
      default:
        return <></>;
    }
  }, [actionModal.action]);

  const handleRowClick = (row: TableDatum) => {
    tableRowNavigation(row, navigate, "/schema/${id}");
  };

  return (
    <Box sx={styles.schemas}>
      <Box>
        <Box sx={styles.header}>
          <Box sx={styles.title}>
            <Heading as="h1">Schemas</Heading>
            {!isLoading && (
              <small>
                {collectionCountLabel(
                  "No schemas",
                  "schema",
                  "schemas",
                  schemas?.length || 0,
                  "No",
                  `schema${dataFiltered.length === 1 ? "" : "s"} found from`,
                  dataFiltered.length
                )}
              </small>
            )}
          </Box>

          <Box sx={styles.actions}>
            <Input
              autoComplete="off"
              className="searchInput"
              icon={<PiMagnifyingGlassBold />}
              type="search"
              placeholder="Search"
              onChange={(e: any) => setFilter(e.target.value)}
            />
            <Button variants={["primary"]} tabIndex={-1} onClick={() => setActionModal({ action: "Create", selectedSchema: null })}>
              Create
            </Button>
          </Box>
        </Box>

        {schemas && (
          <Box sx={{ ...styles.tableWrapper, ...(!!(totalItems && totalItems > itemsPerPage) ? styles.tableWrapperWithPagination : {}) }}>
            <Table
              onRowClick={handleRowClick}
              data={tableData as any}
              columnWidths={["32%", "32%", "31%", "5%"]}
              getRowStyles={() => ({
                cursor: "pointer",
              })}
            />
          </Box>
        )}

        {!!(totalItems && totalItems > itemsPerPage) && (
          <Pagination totalItems={totalItems} itemsPerPage={itemsPerPage} onPageChange={paginate} initialPage={page} />
        )}
      </Box>

      <ActionModal
        title={actionModal.action?.toString() || ""}
        isOpen={!!actionModal.action}
        onClose={() => setActionModal({ action: null, selectedSchema: null })}
        workspaceId={workspaceId}
        schema={actionModal.selectedSchema}
        icon={actionModal.action === "Delete" && <PiTrash color="red" size={sizes.icon.large} />}>
        {modalContent}
      </ActionModal>
    </Box>
  );
}
